import { useCallback, useMemo } from 'react';
import { DndContext, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { ComputedFieldSummaryPayload } from '@rossum/api-client/internal';
import { TileDraggable } from '@rossum/components/tile';
import { Stack } from '@rossum/ui/material';
import { QuerySummaryTile } from './QuerySummaryTile';
import { QueryObject, VariablesMap } from './types';

type AggregationSummaryProps = ComputedFieldSummaryPayload & {
  onChange: (value: {
    queries: QueryObject[];
    variables: VariablesMap;
  }) => void;
  value: {
    queries: QueryObject[];
    variables: VariablesMap;
  };
  disabled?: boolean;
  dataset: string;
  datasetName?: string;
  expandedQueries: Set<number>;
  onExpandedQueriesChange: (value: Set<number>) => void;
  // Where a query is edited is the parent's decision; this only reports which one was asked for.
  onQueryEdit: (queryIndex: number) => void;
};

export const AggregationSummary = ({
  disabled,
  fieldSchemaId,
  schemaContent,
  onChange,
  value,
  dataset = '',
  datasetName,
  expandedQueries,
  onExpandedQueriesChange,
  onQueryEdit,
}: AggregationSummaryProps) => {
  const handleDeleteQuery = useCallback(
    (queryIndex: number) => {
      onChange({
        ...value,
        queries: value.queries.filter((_, index) => index !== queryIndex),
      });
    },
    [onChange, value]
  );

  const handleToggleExpanded = useCallback(
    (queryIndex: number) => {
      const next = new Set(expandedQueries);
      if (next.has(queryIndex)) {
        next.delete(queryIndex);
      } else {
        next.add(queryIndex);
      }
      onExpandedQueriesChange(next);
    },
    [expandedQueries, onExpandedQueriesChange]
  );

  // Generate stable IDs for queries based on content hash only (no index)
  // This ensures reordering doesn't invalidate React Query cache
  const queryIds = useMemo(
    () => value.queries.map(query => `query-${JSON.stringify(query)}`),
    [value.queries]
  );

  const handleDragStart = useCallback(
    (_event: DragStartEvent) => {
      onExpandedQueriesChange(new Set());
    },
    [onExpandedQueriesChange]
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) return;

      const activeIndex = queryIds.indexOf(active.id as string);
      const overIndex = queryIds.indexOf(over.id as string);

      if (activeIndex !== -1 && overIndex !== -1) {
        const reorderedQueries = arrayMove(
          value.queries,
          activeIndex,
          overIndex
        );
        onChange({
          ...value,
          queries: reorderedQueries,
        });
      }
    },
    [queryIds, value, onChange]
  );

  return (
    <Stack>
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext items={queryIds}>
          <Stack spacing={1}>
            {value.queries.map((query, queryIndex) => (
              <TileDraggable
                // oxlint-disable-next-line react/no-array-index-key -- oxlint stricter than eslint — index used as fallback, not sole key
                key={queryIds[queryIndex]}
                id={queryIds[queryIndex] ?? `query-${queryIndex}`}
              >
                <QuerySummaryTile
                  query={query}
                  queryIndex={queryIndex}
                  schemaContent={schemaContent}
                  fieldSchemaId={fieldSchemaId}
                  variables={value.variables}
                  disabled={disabled}
                  isExpanded={expandedQueries.has(queryIndex)}
                  onToggleExpanded={() => handleToggleExpanded(queryIndex)}
                  onDelete={() => handleDeleteQuery(queryIndex)}
                  onEdit={() => onQueryEdit(queryIndex)}
                  dataset={dataset}
                  datasetName={datasetName}
                />
              </TileDraggable>
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
    </Stack>
  );
};
